/**
 * ADLFlowSheetPage
 * CCH Care — Phase 3 → Phase 5e → Late Doc Phase 1 → Late Doc UI surfacing (2026-05-14)
 *
 * Facility-timezone datetime fix (2026-06-25):
 * - The local formatDateTime() used `new Date(dtStr.replace(' ','T'))`,
 *   which the browser interprets in implementation-defined fashion
 *   (effectively the viewer's local zone / UTC) — the exact bug
 *   formatters.js exists to fix. reopen_expires_at is a real server
 *   datetime, so it now renders via the canonical formatDateTime from
 *   formatters.js, which parses in the server frame and displays in the
 *   registered facility zone (set by AuthProvider).
 * - The local formatDate() is RETAINED: it formats shift_date, a
 *   date-ONLY value ("YYYY-MM-DD", no time, no server frame). It must
 *   NOT go through parseServerDt — a zone offset on a bare date can roll
 *   it across a day boundary. Midnight-pinned local rendering is correct.
 *
 * Self-review policy on the co-sign CTA (2026-06-04):
 * - Reads record.self_review_policy (surfaced by adl_build_full_record).
 * - Computes whether THIS viewer authored any section on the record.
 * - 'block' + self-review  → replaces the Review & Co-Sign button with an
 *                            "independent reviewer required" notice (the
 *                            backend would 409 the attempt anyway).
 * - 'flag'  + self-review  → shows the will_self_review advisory chip and
 *                            keeps the button.
 * - 'silent' / no self-review → unchanged button, no marker.
 *
 * Per-shift N/A wiring (2026-05-29):
 * - getPatientADLFlags is called with the current shift_type so the
 *   server returns the resolved `map` for THIS shift.
 *
 * Outgoing-mode shift binding fix (2026-05-29):
 * - The page subscribes to useShift().shift live rather than capturing
 *   nav state at mount. URL deep-link and selector picks are honored as
 *   explicit overrides.
 *
 * Late Doc Phase 2 — supervisor reopen surfacing (2026-05-21).
 * Single-row amendment data fix (2026-05-19).
 * Phase 5e — signSilently(section) after attestation.
 * Late Doc Phase 1 (2026-04-17) — consumes `window` from useADLRecord.
 * Late Doc UI surfacing (2026-05-14) — late-section indicators + banner.
 * Deep-link header backfill (2026-05-16).
 * Historical-record review banner (2026-05-16).
 */

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth, getAuthFacilityId } from '../auth/useAuth';
import { COLORS, HYGIENE_TASKS } from '../../config/constants';
import {
  getSafetyChecklist,
  getPatientADLFlags,
  getAttestations,
  attestNothingToReport,
  retractAttestation,
  reopenADLRecord,
} from '../../api/adl';

import useShift        from '../../hooks/useShift';
import useADLRecord    from '../../hooks/useADLRecord';
import useADLSignature from '../../hooks/useADLSignature';

import ADLProgressBar      from '../../components/ui/ADLProgressBar';
import ADLSignatureModal   from '../../components/ui/ADLSignatureModal';
import ADLSupervisorModal  from '../../components/ui/ADLSupervisorModal';
import ADLAmendmentModal   from '../../components/ui/ADLAmendmentModal';
import ADLReopenModal      from '../../components/ui/ADLReopenModal';
import FloatingPatientChip from '../../components/ui/FloatingPatientChip';

import HygieneForm  from './HygieneForm';
import MealsForm    from './MealsForm';
import VitalsForm   from './VitalsForm';
import {
  RepositioningForm,
  FluidIntakeForm,
  BowelUrinationForm,
  SkinChecksForm,
  SafetyChecksForm,
} from './ADLSectionForms';

import ADLResidentSelector from './ADLResidentSelector';
import TransparencyChip     from '../../components/ui/TransparencyChip';
import { parseServerDt, formatDateTime } from '../../utils/formatters';

const SUPERVISOR_ROLES = ['Care Supervisor', 'Care Admin'];

const SECTION_LABELS = {
  hygiene:       'Hygiene',
  meals:         'Meals',
  repositioning: 'Repositioning',
  fluid_intake:  'Fluids',
  bowel:         'Bowel',
  urination:     'Urine',
  skin_checks:   'Skin',
  safety_checks: 'Safety',
  vitals:        'Vitals',
};

export default function ADLFlowSheetPage() {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { patientId: paramPatientId } = useParams();
  const { user, memberships } = useAuth();

  const headerSentinelRef = useRef(null);

  // ── Resolve current membership ───────────────────────────
  const membershipId  = parseInt(localStorage.getItem('care_membership_id') ?? '0');
  const tenantId      = parseInt(localStorage.getItem('care_tenant_id') ?? '0');
  const currentMembership = memberships.find(
    m => m.membership_id === membershipId && m.tenant_id === tenantId
  ) ?? memberships[0] ?? null;

  const role       = currentMembership?.role ?? '';
  const firstName  = currentMembership?.first_name ?? user?.first_name ?? '';
  const lastName   = currentMembership?.last_name  ?? user?.last_name  ?? '';
  const signerName = [firstName, lastName].filter(Boolean).join(' ') || 'Unknown';
  const isSupervisor = SUPERVISOR_ROLES.includes(role);

  // ── Context from navigation state ───────────────────────
  const navState       = location.state ?? {};
  const urlParams      = new URLSearchParams(location.search);
  const urlShiftId     = Number(urlParams.get('shift_id')) || null;
  const urlShiftType   = urlParams.get('shift_type');

  const initPatientId  = navState.patient_id ?? (paramPatientId ? parseInt(paramPatientId) : null);
  const initFirstName  = navState.first_name ?? '';
  const initLastName   = navState.last_name  ?? '';

  // ── Resident selection state ─────────────────────────────
  const [selectedPatientId, setSelectedPatientId] = useState(initPatientId);
  const [residentName,      setResidentName]      = useState(
    initFirstName ? `${initFirstName} ${initLastName}`.trim() : ''
  );

  // ── Live shift binding ───────────────────────────────────
  const { shift: liveShift, loading: shiftLoading } = useShift();
  const [explicitShiftId,   setExplicitShiftId]   = useState(urlShiftId);
  const [explicitShiftType, setExplicitShiftType] = useState(urlShiftType);
  const [explicitShiftDate, setExplicitShiftDate] = useState(null);

  const selectedShiftId   = explicitShiftId   ?? liveShift?.care_shift_id ?? null;
  const selectedShiftType = explicitShiftType ?? liveShift?.shift_type    ?? null;

  // ── Safety checklist items ───────────────────────────────
  const [checklistItems, setChecklistItems] = useState([]);
  useEffect(() => {
    const facilityId = parseInt(getAuthFacilityId() ?? '0') || null;
    getSafetyChecklist(facilityId)
      .then(d => { if (d.success) setChecklistItems(d.items ?? []); })
      .catch(() => {});
  }, []);

  // ── ADL Record hook ──────────────────────────────────────
  const {
    record, sections, signatures, amendments, adlRecordId,
    loading, error,
    sectionStatus, signedCount,
    supervisorSigned,
    window: docWindow,
    refresh, markSectionSigned,
  } = useADLRecord(selectedPatientId, selectedShiftId, selectedShiftType);

  // ── shiftDate derivation ─────────────────────────────────
  const shiftDate = explicitShiftDate
    ?? liveShift?.shift_date
    ?? record?.shift_date
    ?? null;

  const windowState      = docWindow?.state ?? 'within_shift';
  const windowClosed     = windowState === 'window_closed';
  const inGracePeriod    = windowState === 'grace_period';
  const inReopenedWindow = windowState === 'reopened';
  const isHistorical     = !!docWindow?.historical;

  // ── Signature hook ───────────────────────────────────────
  const {
    pendingSection, isModalOpen,
    initiateSign, cancelSign, confirmSign,
    signSilently,
    supervisorModalOpen,
    initiateSupervisorSign, cancelSupervisorSign, confirmSupervisorSign,
    pendingAmendment, isAmendmentModalOpen,
    initiateAmendment, cancelAmendment, confirmAmendment,
    signing, signError,
    amending, amendError,
  } = useADLSignature(adlRecordId, {
    onSectionSigned: (section, data) => {
      markSectionSigned(section, {
        section,
        signed_at:          data.signed_at,
        role_at_signing:    role,
        first_name:         firstName,
        last_name:          lastName,
        late_documentation: !!data.late_documentation,
      });
    },
    onRecordReviewed: () => refresh(),
    onAmendmentSaved: () => refresh(),
  });

  // ── Deep-link header backfill (2026-05-16) ───────────────
  useEffect(() => {
    if (!record) return;
    if (!residentName && (record.first_name || record.last_name)) {
      const name = `${record.first_name ?? ''} ${record.last_name ?? ''}`.trim();
      if (name) setResidentName(name);
    }
  }, [record, residentName]);

  // ── Care plan flags (per-shift, 2026-05-29) ──────────────
  const [adlFlags, setAdlFlags] = useState({});

  useEffect(() => {
    if (!selectedPatientId) return;
    getPatientADLFlags(selectedPatientId, selectedShiftType)
      .then(d => { if (d.success) setAdlFlags(d.map || {}); })
      .catch(() => {});
  }, [selectedPatientId, selectedShiftType]);

  // ── Shift-level attestations ─────────────────────────────
  const [attestations, setAttestations] = useState({});

  useEffect(() => {
    if (!adlRecordId) return;
    getAttestations(adlRecordId)
      .then(d => { if (d.success) setAttestations(d.map || {}); })
      .catch(() => {});
  }, [adlRecordId]);

  // ── N/A helper ───────────────────────────────────────────
  function isSectionNA(sectionKey) {
    return adlFlags[sectionKey]?.is_applicable === false;
  }

  // ── N/A-aware progress counts ────────────────────────────
  const naSectionCount = useMemo(
    () => Object.values(adlFlags).filter(f => f?.is_applicable === false).length,
    [adlFlags]
  );

  const adjustedSignedCount = signedCount + naSectionCount;
  const adjustedAllSigned   = adjustedSignedCount >= 9;

  // ── Self-review policy (facility-configurable, 2026-06-04) ─
  // record.self_review_policy is surfaced by adl_build_full_record.
  // Compute whether THIS viewer authored any section on the record, so
  // the co-sign CTA can advise ('flag') or be withheld ('block').
  const selfReviewPolicy = record?.self_review_policy ?? 'flag';

  const viewerSelfReviewCount = useMemo(() => {
    return Object.entries(signatures).filter(([key, sig]) =>
      key !== 'supervisor'
      && sig?.signature_type === 'completion'
      && Number(sig?.membership_id) === membershipId
    ).length;
  }, [signatures, membershipId]);

  const viewerWouldSelfReview = viewerSelfReviewCount > 0;

  // Distinct people who documented sections on this record (completion
  // signatures only), for the "Documented by" line on the co-sign CTA —
  // gives the reviewing supervisor the who behind each signed section.
  const documenters = useMemo(() => {
    const seen = new Map();
    Object.entries(signatures).forEach(([key, sig]) => {
      if (key === 'supervisor') return;
      if (sig?.signature_type !== 'completion') return;
      const id = sig?.membership_id;
      if (id == null || seen.has(id)) return;
      const name = [sig.first_name, sig.last_name].filter(Boolean).join(' ').trim();
      seen.set(id, name || `Member ${id}`);
    });
    return Array.from(seen.values());
  }, [signatures]);

  // ── Late-documented sections ─────────────────────────────
  const lateSections = useMemo(() => {
    return Object.entries(signatures)
      .filter(([key, sig]) => key !== 'supervisor' && !!sig?.late_documentation)
      .map(([key]) => key);
  }, [signatures]);

  // ── Attestation handlers ─────────────────────────────────
  const handleAttest = useCallback(async (section, notes = '') => {
    if (!adlRecordId) return;
    try {
      const data = await attestNothingToReport(adlRecordId, section, notes);
      if (data.success) {
        setAttestations(prev => ({
          ...prev,
          [section]: {
            attested_at:      data.attested_at,
            attested_by_name: '',
            notes,
          },
        }));
        await signSilently(section);
      }
    } catch (e) {
      console.error('Attest failed:', e?.response?.data?.error || e.message);
    }
  }, [adlRecordId, signSilently]);

  const handleRetractAttestation = useCallback(async (section) => {
    if (!adlRecordId) return;
    // Don't swallow errors — ADLSectionNA awaits this and surfaces the
    // message (e.g. a 409 when the documentation window has closed).
    await retractAttestation(adlRecordId, section);
    // Optimistically drop the local attestation, then refresh so the
    // now-un-signed section re-pulls as draft/editable (the backend
    // deleted both the attestation and its auto-sign completion signature).
    setAttestations(prev => {
      const next = { ...prev };
      delete next[section];
      return next;
    });
    refresh();
  }, [adlRecordId, refresh]);

  // ── Per-section disabled ─────────────────────────────────
  function sectionDisabled(key) {
    if (windowClosed) return true;
    return sectionStatus[key] === 'signed' || supervisorSigned || isSectionNA(key);
  }

  // ── Sign / amend handler ─────────────────────────────────
  function handleSign(sectionKey) {
    if (sectionKey.startsWith('amend_')) {
      initiateAmendment(sectionKey.replace('amend_', ''));
      return;
    }
    initiateSign(sectionKey);
  }

  // ── Section data for amendment modal ────────────────────
  const amendmentSectionData = useMemo(() => {
    if (!pendingAmendment?.section) return [];
    const data = sections[pendingAmendment.section];
    if (!data) return [];
    if (Array.isArray(data)) return data;
    return [data];
  }, [pendingAmendment, sections]);

  // ── Modal: documented meals ──────────────────────────────
  const documentedMeals = useMemo(() => {
    const meals = sections.meals;
    if (!meals) return [];
    const arr = Array.isArray(meals) ? meals : Object.values(meals);
    return arr.map(m => m?.meal_type ?? m?.type ?? null).filter(Boolean);
  }, [sections.meals]);

  // ── Modal: live safety check count ──────────────────────
  const [liveCheckedSafetyItems, setLiveCheckedSafetyItems] = useState(0);
  useEffect(() => {
    const checks = sections.safety_checks;
    if (!checks) return;
    const arr = Array.isArray(checks) ? checks : Object.values(checks);
    setLiveCheckedSafetyItems(arr.filter(item => item?.is_checked || item?.checked).length);
  }, [sections.safety_checks]);

  const handleSafetyCountChange = useCallback((count) => {
    setLiveCheckedSafetyItems(count);
  }, []);

  // ── Modal: live hygiene documented count ─────────────────
  const [liveHygieneDocumentedCount, setLiveHygieneDocumentedCount] = useState(0);
  useEffect(() => {
    const hygiene = sections.hygiene;
    if (!hygiene) return;
    const arr = Array.isArray(hygiene) ? hygiene : Object.values(hygiene);
    setLiveHygieneDocumentedCount(arr.filter(item => item?.assistance_level).length);
  }, [sections.hygiene]);

  const handleHygieneCountChange = useCallback((count) => {
    setLiveHygieneDocumentedCount(count);
  }, []);

  const handleVitalsCountChange = useCallback((_count) => {}, []);

  const hygieneTotalCount = HYGIENE_TASKS.length;
  const totalSafetyItems  = checklistItems.length;

  // ── Grace-period minutes remaining (server-authoritative) ──
  // Source from window.grace_minutes_remaining, computed in the facility
  // timezone by adl_evaluate_write_window. Do NOT re-parse grace_end here:
  // parseServerDt assumes the Central server frame, but grace_end is in the
  // facility frame (Eastern for Riverside), which over-counted by the offset.
  const minutesRemaining = inGracePeriod
    ? (docWindow?.grace_minutes_remaining ?? null)
    : null;

  // ── Late Doc Phase 2 — Supervisor reopen ─────────────────
  const [reopenModalOpen, setReopenModalOpen] = useState(false);
  const [reopening,       setReopening]       = useState(false);
  const [reopenError,     setReopenError]     = useState(null);

  const handleReopenConfirm = useCallback(async (reason) => {
    if (!adlRecordId) return;
    setReopening(true);
    setReopenError(null);
    try {
      const data = await reopenADLRecord(adlRecordId, reason);
      if (data?.success) {
        setReopenModalOpen(false);
        refresh();
      } else {
        setReopenError(data?.error || 'Reopen failed. Please try again.');
      }
    } catch (e) {
      setReopenError(e?.response?.data?.error || e.message || 'Reopen failed.');
    } finally {
      setReopening(false);
    }
  }, [adlRecordId, refresh]);

  const handleReopenCancel = useCallback(() => {
    if (reopening) return;
    setReopenModalOpen(false);
    setReopenError(null);
  }, [reopening]);

  // ── Resident selected from selector ─────────────────────
  function handleResidentSelected(resident, shift) {
    setSelectedPatientId(resident.patient_id);
    setResidentName(`${resident.first_name} ${resident.last_name}`.trim());
    setExplicitShiftId(shift.care_shift_id);
    setExplicitShiftType(shift.shift_type);
    setExplicitShiftDate(shift.shift_date);
  }

  // ── Loading guards ───────────────────────────────────────
  if (!explicitShiftType && shiftLoading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
        <div style={{ fontSize: 15, color: COLORS.textMuted }}>Loading shift&hellip;</div>
      </div>
    );
  }

  if (!selectedPatientId || !selectedShiftType) {
    return <ADLResidentSelector onSelect={handleResidentSelected} />;
  }

  if (loading && !record) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
        <div style={{ fontSize: 15, color: COLORS.textMuted }}>Loading ADL Flow Sheet&hellip;</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{
          background: COLORS.dangerLight, border: `1px solid ${COLORS.dangerAlpha}`,
          borderRadius: 10, padding: 16,
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.danger }}>⚠️ Failed to load ADL record</div>
          <div style={{ fontSize: 13, color: COLORS.danger, marginTop: 4 }}>{error}</div>
          <button onClick={refresh} style={{
            marginTop: 12, padding: '8px 16px', borderRadius: 8,
            background: COLORS.danger, color: '#fff', border: 'none',
            fontSize: 13, fontWeight: 700, cursor: 'pointer',
          }}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: COLORS.bg, minHeight: '100vh', paddingBottom: 100 }}>

      <FloatingPatientChip
        sentinelRef={headerSentinelRef}
        residentName={residentName}
        shiftType={selectedShiftType}
        signedCount={adjustedSignedCount}
        totalSections={9}
      />

      {/* ── Sticky header ── */}
      <div style={{
        background: COLORS.surface, borderBottom: `1px solid ${COLORS.border}`,
        padding: '12px 16px',
        display: 'flex', alignItems: 'center', gap: 12,
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <button onClick={() => navigate(-1)} style={{
          background: 'none', border: 'none', fontSize: 20,
          cursor: 'pointer', color: COLORS.primary, padding: '2px 6px',
        }}>←</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.text }}>ADL Flow Sheet</div>
          <div style={{ fontSize: 12, color: COLORS.textMuted }}>
            {residentName} &middot; {selectedShiftType} Shift
            {shiftDate ? ` · ${formatDate(shiftDate)}` : ''}
          </div>
        </div>
        <StatusBadge
          record={record}
          allSigned={adjustedAllSigned}
          supervisorSigned={supervisorSigned}
        />
      </div>

      <div ref={headerSentinelRef} style={{ height: 0 }} />

      {/* ── Late Documentation: window closed (recent — Phase 2 zone) ── */}
      {windowClosed && !isHistorical && !supervisorSigned && (
        <div style={{
          margin: '12px 16px', padding: '14px 16px',
          background: COLORS.dangerLight,
          border: `1px solid ${COLORS.danger}`, borderRadius: 12,
          display: 'flex', alignItems: 'flex-start', gap: 12,
        }}>
          <span style={{ fontSize: 20, flexShrink: 0 }}>🔒</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.danger, marginBottom: 4 }}>
              Documentation window has closed
            </div>
            <div style={{ fontSize: 12, color: COLORS.danger, lineHeight: 1.5 }}>
              This shift ended more than 2 hours ago. New entries and signatures
              are no longer accepted.
              {' '}
              {isSupervisor
                ? 'As a supervisor, you can reopen this record so missing documentation can be completed.'
                : 'Please contact your supervisor to request a reopen, or use the amendment flow for corrections.'}
            </div>
            {isSupervisor && (
              <button
                type="button"
                onClick={() => setReopenModalOpen(true)}
                style={{
                  marginTop: 10, padding: '8px 16px', borderRadius: 8,
                  background: COLORS.danger, color: '#fff',
                  border: 'none',
                  fontSize: 13, fontWeight: 700, cursor: 'pointer',
                }}
              >
                🔓 Reopen for Late Documentation
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Late Documentation: reopened (Phase 2 active state) ── */}
      {inReopenedWindow && (
        <div style={{
          margin: '12px 16px', padding: '14px 16px',
          background: COLORS.warningLight,
          border: `1px solid ${COLORS.warning}`, borderRadius: 12,
          display: 'flex', alignItems: 'flex-start', gap: 12,
        }}>
          <span style={{ fontSize: 20, flexShrink: 0 }}>🔓</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.warning, marginBottom: 4 }}>
              Record reopened for late documentation
            </div>
            <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.5 }}>
              {docWindow?.reopen_expires_at
                ? `Documentation is permitted until ${formatDateTime(docWindow.reopen_expires_at)}.`
                : 'Documentation is temporarily permitted.'}
              {' '}Entries created now are flagged as late documentation in the audit trail.
            </div>
          </div>
        </div>
      )}

      {/* ── Historical record — read-only review (Phase 3 zone) ── */}
      {isHistorical && (
        <div style={{
          margin: '12px 16px', padding: '14px 16px',
          background: COLORS.surface,
          border: `1px solid ${COLORS.border}`, borderRadius: 12,
          display: 'flex', alignItems: 'flex-start', gap: 12,
        }}>
          <span style={{ fontSize: 20, flexShrink: 0 }}>🗂️</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>
              Historical record — read-only review
            </div>
            <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.5 }}>
              This shift&apos;s documentation window has closed. The record is
              read-only — corrections must be made through a formal amendment.
            </div>
          </div>
        </div>
      )}

      {/* ── Late Documentation: grace period ── */}
      {inGracePeriod && !windowClosed && (
        <div style={{
          margin: '12px 16px', padding: '12px 16px',
          background: COLORS.warningLight,
          border: `1px solid ${COLORS.warning}`, borderRadius: 12,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 20, flexShrink: 0 }}>⏱️</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.warning }}>
              Shift ended — grace period active
            </div>
            <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>
              {minutesRemaining != null
                ? `Approximately ${minutesRemaining} minute${minutesRemaining === 1 ? '' : 's'} remaining to finish documenting this shift.`
                : 'Limited time remaining to finish documenting this shift.'}
              {' '}
              Entries created now are flagged as late documentation for the audit trail.
            </div>
          </div>
        </div>
      )}

      {/* ── Late Documentation: charted-late summary ── */}
      {lateSections.length > 0 && (
        <div style={{
          margin: '12px 16px 0', padding: '10px 14px',
          background: COLORS.warningLight,
          border: `1px solid ${COLORS.warning}`, borderRadius: 10,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 18, flexShrink: 0 }}>⏱</span>
          <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.5 }}>
            <strong style={{ color: COLORS.warning }}>
              {lateSections.length} section{lateSections.length === 1 ? '' : 's'} charted late
              {' '}
            </strong>
            ({lateSections.map(k => SECTION_LABELS[k] ?? k).join(', ')})
            <span style={{ color: COLORS.textMuted }}>
              {' '}— signed during the grace window or after a supervisor reopen.
            </span>
          </div>
        </div>
      )}

      <ADLProgressBar
        signatures={signatures}
        naFlags={adlFlags}
        onSectionClick={scrollToSection}
      />

      {/* ── Supervisor co-sign CTA ── */}
      {adjustedAllSigned && !supervisorSigned && isSupervisor && (
        selfReviewPolicy === 'block' && viewerWouldSelfReview ? (
          <div style={{
            margin: '12px 16px', padding: '14px 16px',
            background: COLORS.warningLight,
            border: `1px solid ${COLORS.warning}`, borderRadius: 12,
            display: 'flex', alignItems: 'flex-start', gap: 12,
          }}>
            <span style={{ fontSize: 20, flexShrink: 0 }}>🔒</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.warning, marginBottom: 4 }}>
                Independent reviewer required
              </div>
              <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.5 }}>
                You documented {viewerSelfReviewCount} section{viewerSelfReviewCount === 1 ? '' : 's'} on
                this record. This facility requires another supervisor or admin to review and co-sign it.
              </div>
            </div>
          </div>
        ) : (
          <div style={{
            margin: '12px 16px', padding: '14px 16px',
            background: COLORS.primaryLight,
            border: `1px solid ${COLORS.primaryAlpha}`, borderRadius: 12,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div>
              <div style={{
                fontSize: 14, fontWeight: 700, color: COLORS.primary,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                All sections complete ✓
                {selfReviewPolicy === 'flag' && viewerWouldSelfReview && (
                  <TransparencyChip kind="will_self_review" count={viewerSelfReviewCount} />
                )}
              </div>
              <div style={{ fontSize: 12, color: COLORS.textMuted }}>Ready for supervisor review</div>
              {documenters.length > 0 && (
                <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>
                  Documented by {documenters.join(', ')}
                </div>
              )}
            </div>
            <button onClick={initiateSupervisorSign} style={{
              padding: '9px 18px', background: COLORS.primary, color: '#fff',
              border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
            }}>
              Review &amp; Co-Sign
            </button>
          </div>
        )
      )}

      {/* ── Completed banner ── */}
      {supervisorSigned && (
        <div style={{
          margin: '12px 16px', padding: '12px 16px',
          background: COLORS.accentLight, border: `1px solid ${COLORS.accentAlpha}`,
          borderRadius: 12, display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 20 }}>✅</span>
          <div>
            <div style={{
              fontSize: 14, fontWeight: 700, color: COLORS.accent,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              Supervisor Reviewed
              {record?.self_reviewed && (
                <TransparencyChip
                  kind="self_reviewed"
                  count={record.self_reviewed_count}
                />
              )}
            </div>
            <div style={{ fontSize: 12, color: COLORS.textMuted }}>This ADL record is complete.</div>
          </div>
        </div>
      )}

      {/* ── Section Forms ── */}
      <div style={{ padding: '12px 16px' }}>

        <div id="section-hygiene">
          <HygieneForm
            adlRecordId={adlRecordId}
            initialData={sections.hygiene ?? []}
            sectionStatus={sectionStatus.hygiene}
            amendments={amendments}
            onSign={handleSign}
            disabled={sectionDisabled('hygiene')}
            onDocumentedCountChange={handleHygieneCountChange}
            isNA={isSectionNA('hygiene')}
            naReason={adlFlags['hygiene']?.reason}
          />
        </div>

        <div id="section-meals">
          <MealsForm
            adlRecordId={adlRecordId}
            initialData={sections.meals ?? []}
            sectionStatus={sectionStatus.meals}
            amendments={amendments}
            onSign={handleSign}
            disabled={sectionDisabled('meals')}
            isNA={isSectionNA('meals')}
            naReason={adlFlags['meals']?.reason}
            shiftType={selectedShiftType}
          />
        </div>

        <div id="section-repositioning">
          <RepositioningForm
            adlRecordId={adlRecordId}
            initialData={sections.repositioning ?? []}
            sectionStatus={sectionStatus.repositioning}
            amendments={amendments}
            onSign={handleSign}
            disabled={sectionDisabled('repositioning')}
            onDataSaved={refresh}
            isNA={isSectionNA('repositioning')}
            naReason={adlFlags['repositioning']?.reason}
            attestation={attestations['repositioning'] ?? null}
            onAttest={(notes) => handleAttest('repositioning', notes)}
            onRetractAttestation={() => handleRetractAttestation('repositioning')}
          />
        </div>

        <div id="section-fluid_intake">
          <FluidIntakeForm
            adlRecordId={adlRecordId}
            initialData={sections.fluid_intake ?? []}
            sectionStatus={sectionStatus.fluid_intake}
            amendments={amendments}
            onSign={handleSign}
            disabled={sectionDisabled('fluid_intake')}
            onDataSaved={refresh}
            attestation={attestations['fluid_intake'] ?? null}
            onAttest={(notes) => handleAttest('fluid_intake', notes)}
            onRetractAttestation={() => handleRetractAttestation('fluid_intake')}
            isNA={isSectionNA('fluid_intake')}
            naReason={adlFlags['fluid_intake']?.reason}
          />
        </div>

        <div id="section-bowel">
          <BowelUrinationForm
            adlRecordId={adlRecordId}
            initialBowel={sections.bowel}
            initialUrination={sections.urination}
            bowelStatus={sectionStatus.bowel}
            urinationStatus={sectionStatus.urination}
            amendments={amendments}
            onSign={handleSign}
            bowelDisabled={sectionDisabled('bowel')}
            urinationDisabled={sectionDisabled('urination')}
            bowelIsNA={isSectionNA('bowel')}
            urinationIsNA={isSectionNA('urination')}
            bowelNaReason={adlFlags['bowel']?.reason}
            urinationNaReason={adlFlags['urination']?.reason}
            bowelAttestation={attestations['bowel'] ?? null}
            urinationAttestation={attestations['urination'] ?? null}
            onAttestBowel={(notes) => handleAttest('bowel', notes)}
            onAttestUrination={(notes) => handleAttest('urination', notes)}
            onRetractBowel={() => handleRetractAttestation('bowel')}
            onRetractUrination={() => handleRetractAttestation('urination')}
          />
        </div>

        <div id="section-skin_checks">
          <SkinChecksForm
            adlRecordId={adlRecordId}
            initialData={sections.skin_checks ?? []}
            sectionStatus={sectionStatus.skin_checks}
            amendments={amendments}
            onSign={handleSign}
            disabled={sectionDisabled('skin_checks')}
            onDataSaved={refresh}
            isNA={isSectionNA('skin_checks')}
            naReason={adlFlags['skin_checks']?.reason}
            attestation={attestations['skin_checks'] ?? null}
            onAttest={(notes) => handleAttest('skin_checks', notes)}
            onRetractAttestation={() => handleRetractAttestation('skin_checks')}
          />
        </div>

        <div id="section-safety_checks">
          <SafetyChecksForm
            adlRecordId={adlRecordId}
            checklistItems={checklistItems}
            initialData={sections.safety_checks ?? []}
            sectionStatus={sectionStatus.safety_checks}
            amendments={amendments}
            onSign={handleSign}
            disabled={sectionDisabled('safety_checks')}
            onDataSaved={refresh}
            onCheckedCountChange={handleSafetyCountChange}
            attestation={attestations['safety_checks'] ?? null}
            onAttest={(notes) => handleAttest('safety_checks', notes)}
            onRetractAttestation={() => handleRetractAttestation('safety_checks')}
            isNA={isSectionNA('safety_checks')}
            naReason={adlFlags['safety_checks']?.reason}
          />
        </div>

        <div id="section-vitals">
          <VitalsForm
            adlRecordId={adlRecordId}
            initialData={sections.vitals ?? []}
            sectionStatus={sectionStatus.vitals ?? 'draft'}
            amendments={amendments}
            disabled={sectionDisabled('vitals')}
            onDocumentedCountChange={handleVitalsCountChange}
            isNA={isSectionNA('vitals')}
            naReason={adlFlags['vitals']?.reason}
            onSign={(key) => {
              if (key.startsWith('amend_')) {
                initiateAmendment(key.replace('amend_', ''));
                return;
              }
              markSectionSigned(key, {
                section:         key,
                signed_at:       new Date().toISOString(),
                role_at_signing: role,
                first_name:      firstName,
                last_name:       lastName,
              });
            }}
          />
        </div>

      </div>

      {/* ── CNA Signature Modal ── */}
      <ADLSignatureModal
        isOpen={isModalOpen}
        section={pendingSection}
        signerName={signerName}
        signing={signing}
        error={signError}
        shiftType={selectedShiftType}
        documentedMeals={documentedMeals}
        totalSafetyItems={totalSafetyItems}
        checkedSafetyItems={liveCheckedSafetyItems}
        hygieneDocumentedCount={liveHygieneDocumentedCount}
        hygieneTotalCount={hygieneTotalCount}
        onConfirm={confirmSign}
        onCancel={cancelSign}
      />

      {/* ── Amendment Modal ── */}
      <ADLAmendmentModal
        isOpen={isAmendmentModalOpen}
        section={pendingAmendment?.section}
        sectionData={amendmentSectionData}
        amending={amending}
        error={amendError}
        onConfirm={confirmAmendment}
        onCancel={cancelAmendment}
      />

      {/* ── Supervisor Co-sign Modal ── */}
      <ADLSupervisorModal
        isOpen={supervisorModalOpen}
        residentName={residentName}
        shiftType={selectedShiftType}
        shiftDate={shiftDate ? formatDate(shiftDate) : ''}
        signedSections={adjustedSignedCount}
        supervisorName={signerName}
        signing={signing}
        error={signError}
        onConfirm={confirmSupervisorSign}
        onCancel={cancelSupervisorSign}
      />

      {/* ── Supervisor Reopen Modal (Late Doc Phase 2) ── */}
      <ADLReopenModal
        isOpen={reopenModalOpen}
        residentName={residentName}
        shiftType={selectedShiftType}
        shiftDate={shiftDate ? formatDate(shiftDate) : ''}
        reopening={reopening}
        error={reopenError}
        onConfirm={handleReopenConfirm}
        onCancel={handleReopenCancel}
      />

    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────

function StatusBadge({ record, allSigned, supervisorSigned }) {
  if (!record) return null;

  let label, color, bg;

  if (supervisorSigned) {
    label = 'Reviewed';        color = COLORS.primary;  bg = COLORS.primaryLight;
  } else if (allSigned) {
    label = 'Pending Co-Sign'; color = COLORS.primary;  bg = COLORS.primaryLight;
  } else if (record.status === 'amended') {
    label = 'Amended';         color = COLORS.warning;  bg = COLORS.warningLight;
  } else {
    label = 'In Progress';     color = COLORS.warning;  bg = COLORS.warningLight;
  }

  return (
    <span style={{
      fontSize: 11, fontWeight: 700,
      color, background: bg,
      padding: '3px 10px', borderRadius: 20,
    }}>
      {label}
    </span>
  );
}

function scrollToSection(sectionKey) {
  const el = document.getElementById(`section-${sectionKey}`);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

// shift_date is a date-ONLY value ("YYYY-MM-DD"): no time-of-day, no
// server frame. Render it midnight-pinned in the viewer's local zone so
// it never rolls a day under UTC drift. Do NOT route this through
// parseServerDt / the facility display zone — a zone offset on a bare
// date can shift it across a day boundary. (Datetime values like
// reopen_expires_at DO go through formatters.formatDateTime above.)
function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}